import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import {
  fetchPromotionsAccount,
  startPromotionTopUp,
  sponsorListingWithCredit,
} from '../../util/api';

// Import shared components
import { InlineTextButton, PrimaryButton, SecondaryButton } from '../../components';

import css from './FeatureListingPanel.module.css';

const { Money } = sdkTypes;

const createRequestId = () =>
  `${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 10)}`;

/**
 * GardenPlace: "Feature this listing" box for the seller's own listing page.
 * Uses the same promotion credit as the Promote listings page (server/api/promotions.js).
 *
 * - Right after publishing (?justPublished=true) or when coming back from a top-up, it's open.
 * - Otherwise it's a small "Feature this listing" button that opens the box.
 * - Not shown if promotions aren't set up, or the listing isn't published (e.g. pending approval).
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing the seller's own listing entity
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
const FeatureListingPanel = props => {
  const { listing, className } = props;
  const intl = useIntl();
  const location = useLocation();
  const listingId = listing?.id?.uuid;

  const searchParams = parse(location.search);
  const justPublished =
    searchParams.justPublished === 'true' || searchParams.justPublished === true;
  const topUpResult = searchParams.topup;
  const openFromUrl = justPublished || !!topUpResult || searchParams.feature === 'true';

  const [account, setAccount] = useState(null);
  const [isOpen, setIsOpen] = useState(openFromUrl);
  // { days, priceInSubunits } waiting for confirmation
  const [confirming, setConfirming] = useState(null);
  const [inProgress, setInProgress] = useState(false);
  const [topUpInProgress, setTopUpInProgress] = useState(null);
  const [error, setError] = useState(null);
  const [featuredNow, setFeaturedNow] = useState(false);

  // The data comes from the app's own server, so it's loaded in the browser
  useEffect(() => {
    if (!listingId) {
      return;
    }
    Promise.resolve()
      .then(() => fetchPromotionsAccount())
      .then(response => {
        setAccount(response.data);
        // A credit gift from the marketplace was just added: show the box with the message
        if (response.data?.giftsAppliedInSubunits > 0) {
          setIsOpen(true);
        }
      })
      .catch(() => setAccount(null));
  }, [listingId]);

  const listingInfo = account?.listings?.find(l => l.id === listingId);
  if (!account || !listingInfo) {
    // Promotions aren't set up, the data is loading, or the listing isn't published
    return null;
  }

  const { creditInSubunits, currency, sponsorPrices, topUpAmounts } = account;
  const format = amount => formatMoney(intl, new Money(amount, currency));
  const nowSeconds = Date.now() / 1000;
  const isFeatured =
    Number.isFinite(listingInfo.sponsoredUntil) && listingInfo.sponsoredUntil > nowSeconds;
  const featuredUntilText = isFeatured
    ? intl.formatDate(new Date(listingInfo.sponsoredUntil * 1000), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;
  const cheapest = Math.min(...sponsorPrices.map(p => p.priceInSubunits));
  const needsTopUp = creditInSubunits < cheapest;

  const handleFeature = () => {
    setInProgress(true);
    setError(null);
    sponsorListingWithCredit({ listingId, days: confirming.days, requestId: createRequestId() })
      .then(response => {
        const { creditInSubunits: newCredit, sponsoredUntil } = response.data;
        setAccount(prev => ({
          ...prev,
          creditInSubunits: newCredit,
          listings: prev.listings.map(l => (l.id === listingId ? { ...l, sponsoredUntil } : l)),
        }));
        setConfirming(null);
        setFeaturedNow(true);
      })
      .catch(e => setError(e?.data?.code === 'insufficient-credit' ? 'credit' : 'failed'))
      .finally(() => setInProgress(false));
  };

  const handleTopUp = amountInSubunits => {
    setTopUpInProgress(amountInSubunits);
    setError(null);
    // Come back to this listing page after paying
    startPromotionTopUp({ amountInSubunits, returnPath: location.pathname })
      .then(response => window.location.assign(response.data.url))
      .catch(() => {
        setTopUpInProgress(null);
        setError('topUp');
      });
  };

  if (!isOpen) {
    return (
      <div className={classNames(css.collapsed, className)}>
        {isFeatured ? (
          <span className={css.featuredStatus}>
            <FormattedMessage
              id="FeatureListingPanel.featuredUntil"
              values={{ date: featuredUntilText }}
            />
          </span>
        ) : null}
        <InlineTextButton type="button" className={css.openButton} onClick={() => setIsOpen(true)}>
          <FormattedMessage
            id={isFeatured ? 'FeatureListingPanel.extendLink' : 'FeatureListingPanel.featureLink'}
          />
        </InlineTextButton>
      </div>
    );
  }

  return (
    <section className={classNames(css.root, className)}>
      <h2 className={css.heading}>
        <FormattedMessage
          id={
            justPublished && !featuredNow
              ? 'FeatureListingPanel.justPublishedHeading'
              : 'FeatureListingPanel.heading'
          }
        />
      </h2>

      {topUpResult === 'success' ? (
        <p className={css.notice} role="status">
          <FormattedMessage id="PromotionsPage.topUpSuccess" />
        </p>
      ) : topUpResult === 'canceled' ? (
        <p className={css.notice} role="status">
          <FormattedMessage id="PromotionsPage.topUpCanceled" />
        </p>
      ) : null}

      {featuredNow ? (
        <p className={css.success} role="status">
          <FormattedMessage
            id="FeatureListingPanel.featuredNow"
            values={{ date: featuredUntilText }}
          />
        </p>
      ) : (
        <>
          <p className={css.text}>
            {isFeatured ? (
              <FormattedMessage
                id="FeatureListingPanel.featuredUntilLong"
                values={{ date: featuredUntilText }}
              />
            ) : (
              <FormattedMessage id="FeatureListingPanel.intro" />
            )}
          </p>
          {account.giftsAppliedInSubunits > 0 ? (
            <p className={css.notice} role="status">
              <FormattedMessage
                id="PromotionsPage.giftReceived"
                values={{ amount: format(account.giftsAppliedInSubunits) }}
              />
            </p>
          ) : null}
          <p className={css.credit}>
            <FormattedMessage
              id="FeatureListingPanel.credit"
              values={{ credit: format(creditInSubunits) }}
            />
          </p>

          {confirming ? (
            <div className={css.confirm}>
              <p className={css.text}>
                <FormattedMessage
                  id={isFeatured ? 'PromotionsPage.confirmExtend' : 'PromotionsPage.confirmSponsor'}
                  values={{ price: format(confirming.priceInSubunits), days: confirming.days }}
                />
              </p>
              <div className={css.actions}>
                <PrimaryButton
                  type="button"
                  className={css.button}
                  onClick={handleFeature}
                  inProgress={inProgress}
                  disabled={inProgress}
                >
                  <FormattedMessage id="PromotionsPage.confirm" />
                </PrimaryButton>
                <InlineTextButton
                  type="button"
                  className={css.textButton}
                  onClick={() => setConfirming(null)}
                  disabled={inProgress}
                >
                  <FormattedMessage id="PromotionsPage.cancel" />
                </InlineTextButton>
              </div>
            </div>
          ) : (
            <div className={css.actions}>
              {sponsorPrices.map(option => (
                <SecondaryButton
                  key={option.days}
                  type="button"
                  className={css.button}
                  disabled={creditInSubunits < option.priceInSubunits}
                  onClick={() => {
                    setError(null);
                    setConfirming(option);
                  }}
                >
                  <FormattedMessage
                    id={isFeatured ? 'PromotionsPage.extendOption' : 'PromotionsPage.sponsorOption'}
                    values={{ days: option.days, price: format(option.priceInSubunits) }}
                  />
                </SecondaryButton>
              ))}
            </div>
          )}

          {needsTopUp && !confirming ? (
            <div className={css.topUp}>
              <p className={css.text}>
                <FormattedMessage id="FeatureListingPanel.topUpFirst" />
              </p>
              <div className={css.actions}>
                {topUpAmounts.map(amount => (
                  <SecondaryButton
                    key={amount}
                    type="button"
                    className={css.button}
                    onClick={() => handleTopUp(amount)}
                    inProgress={topUpInProgress === amount}
                    disabled={!!topUpInProgress}
                  >
                    <FormattedMessage
                      id="PromotionsPage.topUpOption"
                      values={{ amount: format(amount) }}
                    />
                  </SecondaryButton>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p className={css.error} role="alert">
          <FormattedMessage
            id={
              error === 'credit'
                ? 'PromotionsPage.notEnoughCredit'
                : error === 'topUp'
                ? 'PromotionsPage.topUpFailed'
                : 'PromotionsPage.sponsorFailed'
            }
          />
        </p>
      ) : null}

      <InlineTextButton
        type="button"
        className={css.textButton}
        onClick={() => {
          setIsOpen(false);
          setConfirming(null);
        }}
      >
        <FormattedMessage
          id={featuredNow ? 'FeatureListingPanel.close' : 'FeatureListingPanel.notNow'}
        />
      </InlineTextButton>
    </section>
  );
};

export default FeatureListingPanel;
